import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Cookie, Settings, Eye, Shield, ToggleLeft, Info } from "lucide-react";

const PolitiqueCookies = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* En-tête */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Cookie className="h-12 w-12 text-orange-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Politique des Cookies</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Informations sur l'utilisation des cookies et technologies similaires sur notre site web
          </p>
        </div>

        <div className="space-y-8">
          {/* 1. Qu'est-ce qu'un cookie */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Info className="h-6 w-6 mr-3" />
                1. Qu'est-ce qu'un cookie ?
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) 
                lors de la visite d'un site web. Il permet au site de reconnaître votre navigateur et de conserver 
                certaines informations sur vos préférences ou actions.
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                <p className="text-orange-800">
                  <span className="font-semibold">Important :</span> Les cookies ne peuvent pas endommager votre appareil 
                  ni contenir de virus. Ils ne permettent pas non plus d'accéder à vos fichiers personnels.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 2. Types de cookies utilisés */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Settings className="h-6 w-6 mr-3" />
                2. Types de cookies utilisés par Balinaisa
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Cookies strictement nécessaires</h4>
                    <p className="text-blue-700 text-sm mb-2">
                      Ces cookies sont indispensables au fonctionnement du site web.
                    </p>
                    <ul className="text-blue-700 text-sm space-y-1">
                      <li>• Gestion de session</li>
                      <li>• Sécurité du site</li>
                      <li>• Préférences de langue</li>
                      <li>• Panier d'achat (si applicable)</li>
                    </ul>
                    <p className="text-blue-600 text-xs mt-2 font-medium">
                      ⚠️ Ces cookies ne nécessitent pas votre consentement
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Cookies de performance</h4>
                    <p className="text-green-700 text-sm mb-2">
                      Ces cookies nous aident à améliorer les performances du site.
                    </p>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• Mesure d'audience</li>
                      <li>• Statistiques de visite</li>
                      <li>• Temps de chargement</li>
                      <li>• Pages les plus consultées</li>
                    </ul>
                    <p className="text-green-600 text-xs mt-2 font-medium">
                      ✅ Nécessitent votre consentement
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">Cookies fonctionnels</h4>
                    <p className="text-purple-700 text-sm mb-2">
                      Ces cookies améliorent votre expérience utilisateur.
                    </p>
                    <ul className="text-purple-700 text-sm space-y-1">
                      <li>• Mémorisation des préférences</li>
                      <li>• Personnalisation du contenu</li>
                      <li>• Widgets de réseaux sociaux</li>
                      <li>• Chat en ligne (si applicable)</li>
                    </ul>
                    <p className="text-purple-600 text-xs mt-2 font-medium">
                      ✅ Nécessitent votre consentement
                    </p>
                  </div>

                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Cookies publicitaires</h4>
                    <p className="text-red-700 text-sm mb-2">
                      Ces cookies permettent la diffusion de publicités ciblées.
                    </p>
                    <ul className="text-red-700 text-sm space-y-1">
                      <li>• Publicité personnalisée</li>
                      <li>• Suivi des conversions</li>
                      <li>• Remarketing</li>
                      <li>• Partenaires publicitaires</li>
                    </ul>
                    <p className="text-red-600 text-xs mt-2 font-medium">
                      ✅ Nécessitent votre consentement
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. Finalités d'utilisation */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Eye className="h-6 w-6 mr-3" />
                3. Finalités d'utilisation des cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Balinaisa utilise des cookies pour les finalités suivantes :
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-green-700">Assurer le bon fonctionnement du site</span>
                      <p className="text-gray-600 text-sm">Navigation fluide, sécurité, gestion des sessions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-green-700">Mesurer l'audience</span>
                      <p className="text-gray-600 text-sm">Statistiques de fréquentation, pages populaires</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-green-700">Améliorer l'expérience utilisateur</span>
                      <p className="text-gray-600 text-sm">Personnalisation, préférences, ergonomie</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-green-700">Analyser les performances</span>
                      <p className="text-gray-600 text-sm">Temps de chargement, erreurs, optimisations</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-green-700">Proposer du contenu adapté</span>
                      <p className="text-gray-600 text-sm">Recommandations, contenu personnalisé</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-green-700">Assurer la sécurité</span>
                      <p className="text-gray-600 text-sm">Protection contre les fraudes, authentification</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4. Durée de conservation */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Shield className="h-6 w-6 mr-3" />
                4. Durée de conservation des cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <h4 className="font-semibold text-purple-800 mb-2">Cookies de session</h4>
                  <div className="text-2xl font-bold text-purple-600 mb-2">Temporaire</div>
                  <p className="text-purple-700 text-sm">
                    Supprimés automatiquement à la fermeture du navigateur
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <h4 className="font-semibold text-purple-800 mb-2">Cookies fonctionnels</h4>
                  <div className="text-2xl font-bold text-purple-600 mb-2">6 mois</div>
                  <p className="text-purple-700 text-sm">
                    Mémorisation des préférences utilisateur
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <h4 className="font-semibold text-purple-800 mb-2">Cookies analytiques</h4>
                  <div className="text-2xl font-bold text-purple-600 mb-2">13 mois</div>
                  <p className="text-purple-700 text-sm">
                    Mesure d'audience et statistiques (maximum légal)
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <p className="text-purple-800">
                  <span className="font-semibold">Note importante :</span> La durée de conservation respecte 
                  les recommandations de la CNIL et ne peut excéder 13 mois pour les cookies de mesure d'audience.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 5. Gestion de vos préférences */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <ToggleLeft className="h-6 w-6 mr-3" />
                5. Gestion de vos préférences cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 p-6 rounded-lg">
                <h4 className="font-semibold text-indigo-800 mb-4">Bandeau de consentement</h4>
                <p className="text-indigo-700 mb-4">
                  Lors de votre première visite, un bandeau vous permet de :
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-indigo-700">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                      Accepter tous les cookies
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                      Refuser les cookies non essentiels
                    </li>
                  </ul>
                  <ul className="space-y-2 text-indigo-700">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                      Personnaliser vos choix
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                      Obtenir plus d'informations
                    </li>
                  </ul>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800">Autres moyens de gestion</h4>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-semibold text-gray-800 mb-2">Via votre navigateur</h5>
                    <p className="text-gray-700 text-sm mb-3">
                      Vous pouvez configurer votre navigateur pour :
                    </p>
                    <ul className="text-gray-700 text-sm space-y-1">
                      <li>• Bloquer tous les cookies</li>
                      <li>• Accepter seulement certains cookies</li>
                      <li>• Supprimer les cookies existants</li>
                      <li>• Être alerté avant l'installation</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-semibold text-gray-800 mb-2">Liens utiles par navigateur</h5>
                    <ul className="text-gray-700 text-sm space-y-1">
                      <li>• <span className="font-medium">Chrome :</span> Paramètres &gt; Confidentialité</li>
                      <li>• <span className="font-medium">Firefox :</span> Options &gt; Vie privée</li>
                      <li>• <span className="font-medium">Safari :</span> Préférences &gt; Confidentialité</li>
                      <li>• <span className="font-medium">Edge :</span> Paramètres &gt; Cookies</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                <p className="text-yellow-800">
                  <span className="font-semibold">Attention :</span> La désactivation de certains cookies peut affecter 
                  le bon fonctionnement du site web et limiter l'accès à certaines fonctionnalités.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 6. Cookies tiers */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Settings className="h-6 w-6 mr-3" />
                6. Cookies de services tiers
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Notre site peut intégrer des services fournis par des tiers qui déposent leurs propres cookies :
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold text-red-700">Services d'analyse</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Google Analytics</span>
                        <p className="text-sm text-gray-600">Mesure d'audience et statistiques de visite</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Hotjar</span>
                        <p className="text-sm text-gray-600">Analyse du comportement utilisateur</p>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-red-700">Réseaux sociaux</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Facebook Pixel</span>
                        <p className="text-sm text-gray-600">Suivi des conversions publicitaires</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">LinkedIn Insight</span>
                        <p className="text-sm text-gray-600">Mesure de performance des campagnes</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="text-red-800">
                  <span className="font-semibold">Important :</span> Ces services tiers ont leurs propres politiques 
                  de confidentialité. Nous vous encourageons à les consulter pour comprendre leurs pratiques.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueCookies;